/**
 * MI EXPEDIENTE (E1) — la única pantalla académica del alumno, con pestañas
 * *Avance · Notas · Asistencia · Pagos · Certificados*.
 *
 * Antes esto era una lista suelta de matrículas/asistencia/certificados a la
 * que solo llegaba un representante desde el nav: el alumno no tenía dónde ver
 * lo suyo consolidado, aunque el dato llevara tiempo sincronizado (F3-D). Las
 * pestañas nuevas ensamblan ese dato; las que ya existían en otra pantalla se
 * ENLAZAN, no se copian:
 *
 *  · **Pagos** monta `StudentPayments` (features/finance), la misma pantalla de
 *    `/payments` — no hay un segundo estado de cuenta.
 *  · **Certificados** enlaza a `/verify/:id`, la verificación pública que ya
 *    existe.
 *  · **Avance** reutiliza `CompletionProgress` (el criterio server-authoritative).
 *
 * El selector de PUPILOS del representante sigue viviendo aquí, encima de las
 * pestañas: es la misma pantalla, vista para otra persona.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { BadgeCheck, GraduationCap, Users } from "lucide-solid";
import { attendanceStatsOf, selectWard } from "@space/shared";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { PillTabPanels, PillTabs } from "@/components/ui/pill-tabs";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { StudentPayments } from "@/features/finance/components/student-payments";
import { CompletionProgress } from "../../completion/completion-progress";
import { CohortAttendanceRecord } from "../record/cohort-attendance-record";
import { CohortReportCard } from "../record/cohort-report-card";
import {
  useMyGuardianWards,
  useMyStudentProfile,
  useUserCertificates,
  useUserEnrollments,
} from "../../hooks/use-people-data";

type RecordTab = "progress" | "grades" | "attendance" | "payments" | "certificates";

const TABS: { id: RecordTab; label: MessageDescriptor; feature?: string }[] = [
  { id: "progress", label: msg`Progress` },
  { id: "grades", label: msg`Grades` },
  { id: "attendance", label: msg`Attendance` },
  { id: "payments", label: msg`Payments`, feature: "billing.core" },
  { id: "certificates", label: msg`Certificates` },
];

/** Matrículas con aula viva: son las que tienen notas y asistencia que mirar. */
const OPEN_STATUSES = new Set(["active", "completed"]);

export const AcademicSelfView: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();

  const myProfile = useMyStudentProfile();
  const enrollments = useUserEnrollments(userId);
  const certificates = useUserCertificates(userId);
  const wards = useMyGuardianWards();
  // FLAGS: sin cobros en la academia, la pestaña de pagos no existe (mismo
  // gate que el item de nav «Mis pagos»).
  const billing = useFeatureEnabled("billing.core");

  const [tab, setTab] = createSignal<RecordTab>("progress");
  const tabItems = createMemo(() =>
    TABS.filter((td) => td.feature === undefined || billing().enabled).map((td) => ({
      id: td.id,
      label: t(td.label),
    })),
  );

  const rows = () => enrollments.data ?? [];
  const openRows = createMemo(() =>
    rows().filter((e) => OPEN_STATUSES.has(e.status ?? "") && e.cohortId),
  );

  const isGuardian = () => (wards.data ?? []).length > 0;
  const [selectedWard, setSelectedWard] = createSignal<string | null>(null);
  const activeWard = createMemo(() =>
    selectWard(
      (wards.data ?? []).map((w) => ({
        student_user_id: w.student_user_id,
        is_primary: w.is_primary,
      })),
      selectedWard(),
    ),
  );

  // F4: expediente del pupilo seleccionado — las filas llegan por el stream
  // my_ward_academics (matrículas del pupilo con notas/asistencia denorm).
  const wardEnrollments = useUserEnrollments(() => activeWard() ?? undefined);

  const hasAttendance = () =>
    rows().some((e) => attendanceStatsOf(e.raw as Record<string, unknown>).hasData);

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5">
        <PageHeader
          title={t(msg`My academic record`)}
          description={t(msg`Your progress, grades, attendance, payments and certificates.`)}
          icon={GraduationCap}
        />

        {/* Selector de pupilos (representante) — defensivo */}
        <Show when={isGuardian()}>
          <div class="rounded-2xl border border-border p-4" data-testid="guardian-wards">
            <div class="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Users class="h-4 w-4 text-primary" /> {t(msg`My wards`)}
            </div>
            <div class="flex flex-wrap gap-2">
              <For each={wards.data ?? []}>
                {(w) => (
                  <button
                    type="button"
                    data-testid="ward-chip"
                    onClick={() => setSelectedWard(w.student_user_id)}
                    class={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      activeWard() === w.student_user_id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {w.relationship ?? t(msg`Ward`)}
                  </button>
                )}
              </For>
            </div>
            {/* F4: expediente del pupilo — matrículas, notas y asistencia
                (stream my_ward_academics; asistencia denormalizada F3-A). */}
            <Show when={activeWard()}>
              <div class="mt-3 space-y-2" data-testid="ward-enrollments">
                <Show
                  when={(wardEnrollments.data ?? []).length > 0}
                  fallback={
                    <p class="text-xs text-muted-foreground">
                      {t(msg`No enrollments for this ward yet.`)}
                    </p>
                  }
                >
                  <For each={wardEnrollments.data ?? []}>
                    {(e) => {
                      const stats = attendanceStatsOf(e.raw as Record<string, unknown>);
                      return (
                        <div
                          class="flex items-center justify-between rounded-xl border border-border p-3"
                          data-testid="ward-enrollment-row"
                        >
                          <div>
                            <p class="text-sm font-semibold">{e.cohortName ?? e.cohortId}</p>
                            <p class="text-xs text-muted-foreground">
                              {e.status}
                              <Show when={stats.hasData && stats.rate != null}>
                                {" · "}
                                <span data-testid="ward-attendance-rate">
                                  {t(msg`Attendance`)} {stats.rate}%
                                </span>
                              </Show>
                            </p>
                          </div>
                          <Show
                            when={e.finalGrade != null}
                            fallback={
                              <span class="text-xs text-muted-foreground">
                                {t(msg`In progress`)}
                              </span>
                            }
                          >
                            <span
                              class="rounded-full bg-success/15 px-2 py-0.5 text-sm font-bold text-success"
                              data-testid="ward-final-grade"
                            >
                              {e.finalGrade}
                            </span>
                          </Show>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </Show>
          </div>
        </Show>

        <Show when={!myProfile.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
          <PillTabs
            tabs={tabItems()}
            value={tab()}
            onChange={setTab}
            testIdPrefix="record-tab"
            label={t(msg`Academic record sections`)}
          />

          <PillTabPanels value={tab()} testIdPrefix="record-tab">
            {/* ── Avance ── */}
            <Show when={tab() === "progress"}>
              <section class="space-y-3">
                <h2 class="font-display text-lg font-bold">{t(msg`Enrollments`)}</h2>
                <Show
                  when={rows().length > 0}
                  fallback={
                    <EmptyState
                      icon={GraduationCap}
                      title={t(msg`No enrollments yet`)}
                      description={t(msg`When you enroll in a cohort it will show up here.`)}
                    />
                  }
                >
                  <div class="space-y-2" data-testid="self-enrollments">
                    <For each={rows()}>
                      {(e) => (
                        <div class="space-y-2 rounded-xl border border-border p-3">
                          <div class="flex items-center justify-between">
                            <div>
                              <p class="text-sm font-semibold">{e.cohortName ?? e.cohortId}</p>
                              <p class="text-xs text-muted-foreground">{e.status}</p>
                            </div>
                            <Show
                              when={e.finalGrade != null}
                              fallback={
                                <span class="text-xs text-muted-foreground">
                                  {t(msg`In progress`)}
                                </span>
                              }
                            >
                              <span class="rounded-full bg-success/15 px-2 py-0.5 text-sm font-bold text-success">
                                {e.finalGrade}
                              </span>
                            </Show>
                          </div>
                          {/* M1: progreso de aprobación (reglas de completion). */}
                          <Show
                            when={(e.status === "active" || e.status === "completed") && e.cohortId}
                          >
                            {(cid) => (
                              <>
                                <CompletionProgress cohortId={cid()} />
                                <Link
                                  to={`/classroom/${cid()}`}
                                  class="inline-block text-xs font-semibold text-primary hover:underline"
                                  data-testid="self-open-classroom"
                                >
                                  {t(msg`Open classroom`)} →
                                </Link>
                              </>
                            )}
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </section>
            </Show>

            {/* ── Notas ── */}
            <Show when={tab() === "grades"}>
              <div class="space-y-3" data-testid="self-grades">
                <Show
                  when={openRows().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground">
                      {t(msg`You have no cohort with grades yet.`)}
                    </p>
                  }
                >
                  <For each={openRows()}>
                    {(e) => (
                      <CohortReportCard
                        cohortId={e.cohortId ?? ""}
                        cohortName={e.cohortName ?? e.cohortCode ?? e.cohortId ?? ""}
                      />
                    )}
                  </For>
                </Show>
              </div>
            </Show>

            {/* ── Asistencia ── */}
            <Show when={tab() === "attendance"}>
              <div class="space-y-3" data-testid="self-attendance">
                <Show
                  when={openRows().length > 0 || hasAttendance()}
                  fallback={
                    <p class="text-sm text-muted-foreground">
                      {t(msg`Attendance statistics aren't available yet.`)}
                    </p>
                  }
                >
                  <For each={openRows()}>
                    {(e) => (
                      <CohortAttendanceRecord
                        cohortId={e.cohortId ?? ""}
                        cohortName={e.cohortName ?? e.cohortCode ?? e.cohortId ?? ""}
                      />
                    )}
                  </For>
                </Show>
              </div>
            </Show>

            {/* ── Pagos: la MISMA pantalla que /payments, no una copia ── */}
            <Show when={tab() === "payments"}>
              <div data-testid="self-payments">
                <StudentPayments />
              </div>
            </Show>

            {/* ── Certificados ── */}
            <Show when={tab() === "certificates"}>
              <section class="space-y-3">
                <h2 class="font-display text-lg font-bold">{t(msg`Certificates`)}</h2>
                <Show
                  when={(certificates.data ?? []).length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground">{t(msg`No certificates yet.`)}</p>
                  }
                >
                  <div class="space-y-2" data-testid="self-certificates">
                    <For each={certificates.data ?? []}>
                      {(c) => (
                        <Link
                          to={`/verify/${c.id}`}
                          class="flex items-center justify-between rounded-xl border border-border p-3 hover:border-primary/50"
                        >
                          <span class="flex items-center gap-2 text-sm font-semibold">
                            <BadgeCheck class="h-4 w-4 text-success" /> {c.course_title}
                          </span>
                        </Link>
                      )}
                    </For>
                  </div>
                </Show>
              </section>
            </Show>
          </PillTabPanels>
        </Show>
      </div>
    </PageContainer>
  );
};
